import { rem } from 'polished'
import React, { useCallback, useContext, useEffect, useRef } from 'react'
import styled from 'styled-components'
import Pane from 'components/Common/Pane'
import LayoutContainer from 'components/Common/LayoutContainer'
import { TourV2ContentSections, TOUR_V2_CONTENT_SECTION_LABELS } from 'constants/tours'
import Heading from 'components/Luxkit/Typography/Heading'
import cn from 'clsx'
import zIndex from 'styles/tools/z-index'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { HeaderHeightContext } from 'contexts/HeaderStateContext'
import { getImmersiveTripId } from 'tripPlanner/selectors'

import TabButtonsGroup from 'components/Luxkit/Tabs/TabButtonsGroup'
import TabButton from 'components/Luxkit/Tabs/TabButton'
import { centreElementInView } from 'lib/window/scrollUtils'
import ContactOptions from 'components/Common/ContactOptions'
import TextButton from 'components/Luxkit/Button/TextButton'
import FormatCurrency from 'components/Common/FormatCurrency'
import BodyText from 'components/Luxkit/Typography/BodyText'
import useTourV2PurchasableOptionDetails from 'hooks/TourV2/useTourV2PurchasableOptionDetails'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import { getIsTourOfferPurchasable } from 'selectors/TourV2/tourV2OfferPageSelectors'
import PrimaryButtonPaletteProvider, { PrimaryButtonPalette } from 'contexts/PrimaryButtonPaletteProvider'
import { useAppSelector } from 'hooks/reduxHooks'

const StickyPane = styled(Pane)`
  position: fixed;
  width: 100vw;
  z-index: ${zIndex.subHeader};
  top: 0;
  transition: transform 500ms ease-in-out;
  box-shadow: ${props => props.theme.shadow.bottom.small};
  /* Move offscreen by an extra 10px so the shadow isn't visible when header is hidden */
  transform: translateY(calc(-100% - 10px));

  &.visible {
    transform: translateY(0);
  }
`

const StyledLayoutContainer = styled(LayoutContainer)`
  margin-top: ${rem(20)};
`

const ContactLayoutContainer = styled(LayoutContainer)`
  display: flex;
  justify-content: end;
`

const ContactBanner = styled.div`
  background-color: ${({ theme }) => theme.palette.neutral.seven};
`

const HeaderTitle = styled(Heading)`
  flex-grow: 1;
  align-self: stretch;
`

const PricingContainer = styled(CSSBreakpoint)`
  flex-shrink: 0;
`

const ContentContainer = styled.div`
  display: flex;
  gap: ${rem(12)};
  align-items: center;
`

interface MappedStateProps {
  isOfferPurchasable: boolean
}

interface Props {
  activeSection?: TourV2ContentSections
  contentSections: TourV2ContentSections[]
  onCheckAvailability: () => void
  onScrollTo: (section: TourV2ContentSections) => void
  purchasableOption?: Tours.V2OfferPurchasableOption
  title: string
  visible: boolean
  buttonPaletteValue?: PrimaryButtonPalette
}

function TourV2StickyHeader(props: Props & MappedStateProps) {
  const {
    activeSection,
    contentSections,
    isOfferPurchasable,
    onCheckAvailability,
    onScrollTo,
    purchasableOption,
    title,
    visible,
    buttonPaletteValue,
  } = props

  const tabButtonsRef = useRef<Record<string, HTMLButtonElement>>({})
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (visible) {
      if (ref.current) {
        document.documentElement.style.scrollPaddingTop = rem(ref.current.offsetHeight)
      }
      return () => {
        document.documentElement.style.scrollPaddingTop = rem(12)
      }
    }
  }, [visible])

  useEffect(() => {
    centreElementInView(tabButtonsRef.current[activeSection])
  }, [activeSection])

  const purchasableOptionDetails = useTourV2PurchasableOptionDetails(purchasableOption)

  const currentTripId = useAppSelector(getImmersiveTripId)
  const { heights: { tripPlannerImmersive: tripPlannerImmersiveHeaderHeight } } = useContext(HeaderHeightContext)

  const offset = currentTripId ? tripPlannerImmersiveHeaderHeight : 0

  const scrollToDepartures = useCallback(
    () => {
      onCheckAvailability() // Track click on "Check availability" button
      onScrollTo(TourV2ContentSections.DEPARTURES)
    },
    [onScrollTo, onCheckAvailability],
  )

  return (
    <PrimaryButtonPaletteProvider palette={buttonPaletteValue}>
      <StickyPane
        ref={ref}
        style={{ top: offset } as React.CSSProperties}
        className={cn({ visible })}
        data-testid="tour-v2-sticky-header"
      >
        <ContactBanner>
          <ContactLayoutContainer>
            <ContactOptions />
          </ContactLayoutContainer>
        </ContactBanner>
        <StyledLayoutContainer>
          <ContentContainer>
            <HeaderTitle variant="heading5" colour="neutral-one">{title}</HeaderTitle>

            <PricingContainer min="tablet">
              {!!purchasableOptionDetails && <>
                <div>
                  <BodyText variant="medium" weight="semi-bold" colour="neutral-two" as="span">
                    From{' '}
                  </BodyText>
                  <Heading variant="heading5" colour="neutral-one" as="span">
                    <FormatCurrency value={purchasableOptionDetails.pricePerPerson} format="roundedDollar" />
                  </Heading>
                  <BodyText variant="medium" weight="semi-bold" as="span">/person</BodyText>
                </div>
                <BodyText variant="small" colour="neutral-two" align="end">
                  {purchasableOptionDetails.roomTypeName}
                </BodyText>
              </>}

              {!isOfferPurchasable && <Heading variant="heading5" align="center">
                Sold Out
              </Heading>}
            </PricingContainer>

            {isOfferPurchasable && <CSSBreakpoint min="tablet">
              <TextButton kind="primary" onClick={scrollToDepartures}>
                Check Availability
              </TextButton>
            </CSSBreakpoint>}
          </ContentContainer>
          <TabButtonsGroup>
            {contentSections.map(contentSection => <TabButton
            key={contentSection}
            ref={(element) => { tabButtonsRef.current[contentSection] = element }}
            size="medium"
            isActive={contentSection === activeSection}
            onClick={() => onScrollTo(contentSection)}
          >
              {TOUR_V2_CONTENT_SECTION_LABELS[contentSection]}
            </TabButton>)}
          </TabButtonsGroup>
        </StyledLayoutContainer>
      </StickyPane>
    </PrimaryButtonPaletteProvider>
  )
}

export default withOfferPageState<MappedStateProps>((offerPageState) => ({
  isOfferPurchasable: getIsTourOfferPurchasable(offerPageState),
}))(TourV2StickyHeader)
